import { useEffect, useRef, useState } from "react";
import { X, PaintBrush, Eraser, ArrowUUpLeft, FloppyDisk, ArrowLeft } from "@phosphor-icons/react";
import { fileUrl } from "@/lib/api";

const COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#3b82f6", "#a855f7", "#ffffff", "#000000"];

export default function PhotoAnnotator({ imagePath, onClose, onSave }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [color, setColor] = useState("#ef4444");
  const [width, setWidth] = useState(6);
  const [strokes, setStrokes] = useState([]); // {points:[{x,y}], color, width}
  const drawingRef = useRef(false);
  const currentStrokeRef = useRef(null);
  const imgRef = useRef(null);

  // Intercept the phone's back button so it closes the annotator instead of
  // navigating away from the inspection. Push a sentinel history entry when we open,
  // then handle popstate.
  useEffect(() => {
    const sentinel = { annotator: true, ts: Date.now() };
    try { window.history.pushState(sentinel, ""); } catch (e) {}
    const onPop = (e) => {
      e.preventDefault?.();
      if (onClose) onClose();
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      // If we're still on the sentinel entry when unmounting normally (via Save/X),
      // pop it so the user's next back press behaves normally.
      try {
        if (window.history.state && window.history.state.annotator) {
          window.history.back();
        }
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgRef.current = img;
      const canvas = canvasRef.current;
      if (!canvas) return;
      // scale to container width, keep aspect
      const maxW = Math.min(window.innerWidth - 32, 900);
      const maxH = window.innerHeight - 220;
      let w = img.width, h = img.height;
      const ratio = Math.min(maxW / w, maxH / h, 1);
      canvas.width = w * ratio;
      canvas.height = h * ratio;
      setImgLoaded(true);
      redraw([]);
    };
    img.src = fileUrl(imagePath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagePath]);

  const redraw = (strokeList) => {
    const canvas = canvasRef.current;
    if (!canvas || !imgRef.current) return;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(imgRef.current, 0, 0, canvas.width, canvas.height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (const s of strokeList) {
      ctx.strokeStyle = s.color;
      ctx.lineWidth = s.width;
      ctx.beginPath();
      s.points.forEach((p, i) => {
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.stroke();
    }
  };

  const pointerPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return {
      x: (t.clientX - rect.left) * (canvas.width / rect.width),
      y: (t.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    currentStrokeRef.current = { color, width, points: [pointerPos(e)] };
  };
  const moveDraw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    currentStrokeRef.current.points.push(pointerPos(e));
    redraw([...strokes, currentStrokeRef.current]);
  };
  const endDraw = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (currentStrokeRef.current && currentStrokeRef.current.points.length > 1) {
      setStrokes(s => [...s, currentStrokeRef.current]);
    }
    currentStrokeRef.current = null;
  };

  const undo = () => {
    const next = strokes.slice(0, -1);
    setStrokes(next);
    redraw(next);
  };
  const clearAll = () => {
    setStrokes([]);
    redraw([]);
  };
  const save = async () => {
    if (strokes.length === 0) return;
    canvasRef.current.toBlob(async (blob) => {
      const file = new File([blob], `annotated-${Date.now()}.png`, { type: "image/png" });
      await onSave(file);
    }, "image/png", 0.92);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex flex-col" data-testid="annotator-modal">
      <div className="p-3 flex items-center justify-between bg-[#0f172a] text-white gap-2">
        <button onClick={onClose} data-testid="annotator-back"
          className="tactile-btn h-11 px-3 bg-white/10 hover:bg-white/20 border-2 border-white/30 rounded-lg flex items-center gap-2 font-black uppercase text-xs tracking-widest">
          <ArrowLeft weight="bold" size={18}/> Back
        </button>
        <div className="font-display font-black uppercase tracking-wider text-sm sm:text-base">Mark up photo</div>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10" data-testid="annotator-close">
          <X weight="bold" size={22}/>
        </button>
      </div>

      <div ref={containerRef} className="flex-1 overflow-auto flex items-center justify-center p-2">
        {!imgLoaded && <div className="text-white font-bold">Loading photo...</div>}
        <canvas
          ref={canvasRef}
          className="touch-none bg-white border-2 border-white"
          data-testid="annotator-canvas"
          onMouseDown={startDraw}
          onMouseMove={moveDraw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={moveDraw}
          onTouchEnd={endDraw}
        />
      </div>

      <div className="p-3 bg-[#0f172a] text-white space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <PaintBrush weight="bold" size={20}/>
          {COLORS.map(c => (
            <button key={c} onClick={() => setColor(c)}
              data-testid={`color-${c.replace("#", "")}`}
              className={`h-8 w-8 rounded-full border-2 ${color === c ? "border-white ring-2 ring-[#00C4CC]" : "border-white/40"}`}
              style={{ backgroundColor: c }}/>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <input type="range" min="2" max="20" value={width} onChange={e => setWidth(Number(e.target.value))}
              data-testid="brush-size" className="w-24"/>
            <span className="text-xs font-bold w-6 text-right">{width}px</span>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button onClick={undo} disabled={strokes.length === 0} data-testid="annotator-undo"
            className="h-11 border-2 border-white/60 font-black uppercase text-xs tracking-wider disabled:opacity-40 flex items-center justify-center gap-1">
            <ArrowUUpLeft weight="bold" size={16}/> Undo
          </button>
          <button onClick={clearAll} disabled={strokes.length === 0} data-testid="annotator-clear"
            className="h-11 border-2 border-white/60 font-black uppercase text-xs tracking-wider disabled:opacity-40 flex items-center justify-center gap-1">
            <Eraser weight="bold" size={16}/> Clear
          </button>
          <button onClick={save} disabled={strokes.length === 0} data-testid="annotator-save"
            className="tactile-btn h-11 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-wider disabled:opacity-40 flex items-center justify-center gap-1">
            <FloppyDisk weight="bold" size={16}/> Save
          </button>
        </div>
      </div>
    </div>
  );
}
